// frontend/src/pages/admin/AdvertisementsPage/AdvertisementsPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import Button from '../../../components/common/Button';
import Icon from '../../../components/common/Icon';
import Input from '../../../components/common/Input';
import Pagination from '../../../components/common/Pagination';
import Modal from '../../../components/common/Modal';
import FileUpload from '../../../components/common/FileUpload';
import { getAdvertisementsApi, createAdvertisementApi, deleteAdvertisementApi, uploadAdvertisementImageApi } from '../../../api/advertisement.api';
import { AD_TYPE_LABELS, AD_STATUS_LABELS } from '../../../utils/constants';

const emptyForm = { title: '', type: 'BANNER', link: '', startDate: '', endDate: '', priority: 0, targetPage: '' };

export default function AdvertisementsPage() {
  const [ads, setAds] = useState([]);
  const [filters, setFilters] = useState({ status: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadAds = useCallback(async () => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const { data } = await getAdvertisementsApi({ ...clean, page: pagination.page, limit: pagination.limit });
      setAds(data.data);
      setPagination(prev => ({ ...prev, total: data.meta.total, totalPages: data.meta.totalPages }));
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت تبلیغات');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => { loadAds(); }, [loadAds]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await createAdvertisementApi({
        ...form,
        priority: Number(form.priority),
      });
      if (imageFile) await uploadAdvertisementImageApi(res.data.id, imageFile);
      setSnackbar('تبلیغ ایجاد شد');
      setFormOpen(false);
      setForm(emptyForm);
      setImageFile(null);
      setImagePreview(null);
      loadAds();
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAdvertisementApi(deleteTarget);
      setSnackbar('تبلیغ متوقف شد');
      setDeleteTarget(null);
      loadAds();
    } catch (err) {
      setSnackbar(err.response?.data?.message);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">مدیریت تبلیغات</h1>
        <Button variant="primary" onClick={() => { setFormOpen(true); }}>
          <Icon name="plus" className="ml-2 h-4 w-4" /> تبلیغ جدید
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <select
          className="rounded-xl border border-slate-200 p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">همه وضعیت‌ ها</option>
          {Object.entries(AD_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {snackbar && (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">
          {snackbar}
          <button className="mr-2" onClick={() => setSnackbar('')}>✕</button>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 overflow-x-auto">
        {loading ? (
          <div className="py-8 text-center text-text-dark/50">در حال بارگذاری...</div>
        ) : (
          <>
            <table className="w-full text-right text-sm">
              <thead className="bg-slate-50 text-text-dark/70">
                <tr>
                  <th className="px-4 py-3 font-medium">عنوان</th>
                  <th className="px-4 py-3 font-medium">نوع</th>
                  <th className="px-4 py-3 font-medium">بازه</th>
                  <th className="px-4 py-3 text-center font-medium">بازدید</th>
                  <th className="px-4 py-3 text-center font-medium">کلیک</th>
                  <th className="px-4 py-3 font-medium">وضعیت</th>
                  <th className="px-4 py-3 text-center font-medium">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {ads.map(ad => (
                  <tr key={ad.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3">{ad.title}</td>
                    <td className="px-4 py-3">{AD_TYPE_LABELS[ad.type]}</td>
                    <td className="px-4 py-3">
                      {new Date(ad.startDate).toLocaleDateString('fa-IR')} - {new Date(ad.endDate).toLocaleDateString('fa-IR')}
                    </td>
                    <td className="px-4 py-3 text-center">{ad.views}</td>
                    <td className="px-4 py-3 text-center">{ad.clicks}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                        ad.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' :
                        ad.status === 'PAUSED' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {AD_STATUS_LABELS[ad.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => setDeleteTarget(ad.id)} className="text-red-500 hover:text-red-700 p-1">
                        <Icon name="trash" className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {ads.length === 0 && (
                  <tr>
                    <td colSpan="7" className="py-8 text-center text-text-dark/40">
                      تبلیغی ثبت نشده است
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title="تبلیغ جدید">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
          <Input label="عنوان" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          <select
            className="w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          >
            {Object.entries(AD_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <Input label="لینک" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} />
          <Input label="صفحه هدف" value={form.targetPage} onChange={(e) => setForm({ ...form, targetPage: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="تاریخ شروع" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} required />
            <Input label="تاریخ پایان" type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} required />
          </div>
          <Input label="اولویت (عدد)" type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
          <FileUpload
            label="تصویر تبلیغ"
            onFileSelect={(file) => { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }}
            preview={imagePreview}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={() => setFormOpen(false)}>انصراف</Button>
            <Button variant="primary" type="submit">ثبت</Button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="تأیید توقف تبلیغ">
        <p className="text-text-dark/70">آیا از توقف این تبلیغ مطمئن هستید؟</p>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)}>انصراف</Button>
          <Button variant="primary" onClick={handleDelete}>تأیید</Button>
        </div>
      </Modal>
    </div>
  );
}